import { defaultAxleRole, type TyreLayoutAxleRole, type TyreLayoutSubjectKind } from '@chukua/common';
import clsx from 'clsx';
import { lazy, Suspense, useMemo } from 'react';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import { PlanDiagram } from '../components/PlanDiagram';
import { useRenderQuality } from '../hooks/use-render-quality';
import { placeVehicle, rigColours, towedOffset, vehicleColours, type VehicleAxleInput, type VehicleLayout } from '@chukua/vehicle-model';
import type { ScenePart } from '../adapters/build-scene-vehicle';
import type { VehicleCameraView } from '../policies/camera-poses';
import { asTowedLayout, partOfCode, qualifyCode, type DiagramPart } from '../policies/towed-codes';
import { bodyStyleFor, canCarryContainer, type VehicleBodyStyle } from '@chukua/vehicle-model';

const VehicleViewer = lazy(() => import('../components/VehicleViewer').then((module) => ({ default: module.VehicleViewer })));

export type TyreDiagramView = VehicleCameraView | 'plan';

export interface TyreDiagramAxle {
  /** Omitted: the kind's default for that place (a truck's first axle steers, the rest drive). */
  role?: TyreLayoutAxleRole;
  positions: { code: string; name: string }[];
}

/** The trailer a truck pulls, drawn behind it on the same vehicle (gitaumoses4/chukua#390). */
export interface TyreDiagramTowed {
  bodyStyle?: string | null;
  axles: TyreDiagramAxle[];
  registration?: string | null;
  color?: string | null;
  /** The trailer's own position code, when the selected tyre is one of the trailer's. */
  selectedCode?: string | null;
}

interface TyrePositionDiagramProps {
  subjectKind: TyreLayoutSubjectKind;
  /** The truck class's or trailer type's body style; unknown or missing draws the kind's default. */
  bodyStyle?: string | null;
  axles: TyreDiagramAxle[];
  /** 3D by default; `plan` is the top-down chart. */
  view?: TyreDiagramView;
  /** Case-insensitive — the tyre shown selected, e.g. the position a tyre record is being recorded against. */
  selectedCode?: string | null;
  /** `part` says whose code it is when a trailer is drawn behind the truck: `main` is the truck's, `towed` the trailer's. */
  onSelect?: (code: string, part: DiagramPart) => void;
  showCodes?: boolean;
  isSeeThrough?: boolean;
  showContainer?: boolean;
  /** A particular truck's or trailer's registration, shown on its number plates in 3D; omitted for a class or type. */
  registration?: string | null;
  /** That vehicle's own paint (`#RRGGBB`), for its cab or body in 3D. */
  color?: string | null;
  /** A truck's trailer, drawn coupled behind it; its tyres are picked and selected as the trailer's. */
  towed?: TyreDiagramTowed | null;
  className?: string;
}

function ViewerSkeleton() {
  return (
    <SkeletonRegion label="Loading 3D model" className="h-full w-full">
      <Skeleton className="h-full w-full" />
    </SkeletonRegion>
  );
}

function usePlacedVehicle(subjectKind: TyreLayoutSubjectKind, style: VehicleBodyStyle, axles: TyreDiagramAxle[]): VehicleLayout {
  /** By value: an editor hands over a fresh array on every keystroke, and the 3D model should rebuild only when the shape changes. */
  const axlesKey = JSON.stringify(axles.map((axle, i) => ({ role: axle.role ?? defaultAxleRole(subjectKind, i), positions: axle.positions })));
  return useMemo(() => placeVehicle(subjectKind, style, JSON.parse(axlesKey) as VehicleAxleInput[]), [subjectKind, style, axlesKey]);
}

function useTowedLayout(towed: TyreDiagramTowed | null | undefined): VehicleLayout | null {
  const layout = usePlacedVehicle('trailer_type', bodyStyleFor('trailer_type', towed?.bodyStyle), towed?.axles ?? []);
  return towed ? layout : null;
}

/** What the viewer draws: the vehicle, then its trailer behind it with its codes kept apart from the truck's. */
function useScene(props: TyrePositionDiagramProps, main: VehicleLayout, towed: VehicleLayout | null) {
  const { registration = null } = props;
  const towedRegistration = props.towed?.registration ?? null;
  const parts = useMemo((): ScenePart[] => {
    const own: ScenePart = { layout: main, offset: 0, registration };
    return towed ? [own, { layout: asTowedLayout(towed), offset: towedOffset(main, towed), registration: towedRegistration }] : [own];
  }, [main, towed, registration, towedRegistration]);
  return { parts, colours: sceneColours(props, main, towed), selected: sceneSelection(props) };
}

/** Each vehicle in its own paint: the truck's cab, and the trailer's body when one is drawn behind it. */
function sceneColours(props: TyrePositionDiagramProps, main: VehicleLayout, towed: VehicleLayout | null) {
  const color = props.color ?? null;
  if (!towed) return vehicleColours(main.kind, color);
  const towedColor = props.towed?.color ?? null;
  return rigColours({ truck: color ? { color } : null, trailer: towedColor ? { color: towedColor } : null });
}

/** The selected tyre as the drawing knows it: the trailer's, when the trailer's is selected, else the vehicle's own. */
function sceneSelection(props: TyrePositionDiagramProps): string | null {
  const towedCode = props.towed?.selectedCode;
  const selected = towedCode ? qualifyCode(towedCode, 'towed') : props.selectedCode;
  return selected?.trim().toUpperCase() ?? null;
}

/** Both plans, trailer then truck, as they stand with the front on the right. */
function PlanFallback({
  main,
  towed,
  selected,
  onSelect,
}: {
  main: VehicleLayout;
  towed: VehicleLayout | null;
  selected: string | null;
  onSelect?: TyrePositionDiagramProps['onSelect'];
}) {
  if (!towed) return <PlanDiagram layout={main} selectedCode={selected} onSelect={onSelect && ((code) => onSelect(code, 'main'))} />;
  const pick = selected ? partOfCode(selected) : null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <PlanDiagram layout={towed} selectedCode={pick?.part === 'towed' ? pick.code : null} onSelect={onSelect && ((code) => onSelect(code, 'towed'))} />
      <PlanDiagram layout={main} selectedCode={pick?.part === 'main' ? pick.code : null} onSelect={onSelect && ((code) => onSelect(code, 'main'))} />
    </div>
  );
}

/**
 * A tyre position diagram (gitaumoses4/chukua#176): the vehicle in 3D, or the
 * plan chart, from the body style and the axle list. Props only — no fetch,
 * router or organization context — so a truck page, the yard surface or a map
 * can draw the same vehicle the office editor does. Where the browser has no
 * WebGL, every view is the plan chart.
 */
export function TyrePositionDiagram(props: TyrePositionDiagramProps) {
  const { subjectKind, axles, view = '3d', className } = props;
  const quality = useRenderQuality();
  const style = bodyStyleFor(subjectKind, props.bodyStyle);
  const layout = usePlacedVehicle(subjectKind, style, axles);
  const towed = useTowedLayout(props.towed);
  const scene = useScene(props, layout, towed);
  const onSelect =
    props.onSelect &&
    ((code: string) => {
      const picked = partOfCode(code);
      props.onSelect?.(picked.code, picked.part);
    });
  if (!axles.some((axle) => axle.positions.length > 0)) {
    return (
      <div className={clsx('flex items-center justify-center rounded-md border border-line bg-surface p-6 text-sm text-muted', className)}>
        No axles defined
      </div>
    );
  }
  if (view === 'plan' || quality === 'unsupported') {
    return (
      <div className={className}>
        {view !== 'plan' && <p className="mt-0 mb-2 text-xs text-muted">This browser can't show the 3D model, so here is the plan.</p>}
        <PlanFallback main={layout} towed={towed} selected={scene.selected} onSelect={props.onSelect} />
      </div>
    );
  }
  return (
    <div className={clsx('h-[clamp(320px,58vh,600px)]', className)}>
      <Suspense fallback={<ViewerSkeleton />}>
        <VehicleViewer
          parts={scene.parts}
          colours={scene.colours}
          view={view}
          quality={quality}
          selectedCode={scene.selected}
          onSelect={onSelect}
          showCodes={props.showCodes !== false}
          isSeeThrough={props.isSeeThrough === true}
          showContainer={props.showContainer === true && (canCarryContainer(style) || (towed !== null && canCarryContainer(towed.bodyStyle)))}
        />
      </Suspense>
    </div>
  );
}
